/**
 * <quick-add-modal>
 * ---------------------------------------------------------------------------
 * Compra rápida desde una tarjeta de producto con variantes.
 *
 * Se abre desde cualquier `[data-quick-add]`, que es un ENLACE a la ficha del
 * producto. Sin JavaScript ese enlace navega y el cliente compra en la ficha de
 * siempre; con JavaScript se intercepta el clic y se abre este diálogo con el
 * selector de variantes dentro. Mismo planteamiento que assets/drawer.js.
 *
 * El contenido lo calcula el servidor (`?section_id=quick-add`), no se dibuja
 * aquí: es la misma regla que sigue la cesta en assets/cart.js, y es lo que
 * evita duplicar el formateo de precios y la lógica de disponibilidad.
 *
 * Usa <dialog> nativo a propósito. `showModal()` ya trae foco atrapado, Escape,
 * bloqueo del scroll de fondo y capa superior, así que aquí NO se toca
 * `overflow`, ni se monta una trampa de foco, ni se escucha Escape. Duplicar
 * cualquiera de esas cosas pelearía con el navegador y con el panel de la cesta.
 */

// Envuelto en IIFE por el mismo motivo que cart.js y drawer.js: el editor de
// temas reinyecta el mismo <script> y dos ejecuciones chocan en el ámbito global.
(() => {


class QuickAddModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-quick-add-body]');
    if (!this.dialog || !this.body) return;

    this.querySelectorAll('[data-quick-add-close]').forEach((button) =>
      button.addEventListener('click', () => this.dialog.close())
    );

    // Clic en el fondo. El <dialog> ocupa toda la pantalla y el panel visible es
    // su hijo, así que un clic cuyo objetivo sea el propio diálogo cae fuera.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    // Vaciar SIEMPRE al cerrar. Si se acumulasen dos contenidos, habría dos
    // `#price-quick-add` y dos <variant-picker> con el mismo id de sección:
    // `getElementById` devuelve el primero, así que el precio del segundo
    // producto acabaría pintándose sobre la ficha del primero.
    this.dialog.addEventListener('close', () => {
      this.body.innerHTML = '';
    });

    // El panel de la cesta se abre justo después de añadir; el diálogo tiene que
    // haberse cerrado antes, porque mientras está en modo modal el resto de la
    // página es inerte y el panel no podría recibir el foco.
    document.addEventListener('cart:added', () => {
      if (this.dialog.open) this.dialog.close();
    });

    // Un único delegado global, como en drawer.js: así funcionan también las
    // tarjetas que llegan al DOM más tarde (una sección recargada, la búsqueda
    // predictiva, una página más de resultados).
    if (!document.body.dataset.quickAddDelegate) {
      document.body.dataset.quickAddDelegate = 'true';
      document.addEventListener('click', this.#onDocumentClick);
    }
  }

  #onDocumentClick = (event) => {
    const trigger = event.target.closest('[data-quick-add]');
    if (!trigger) return;

    const modal = document.querySelector('quick-add-modal');
    if (!modal?.dialog) return; // sin diálogo en la página, el enlace navega

    event.preventDefault();
    modal.open(trigger);
  };

  async open(trigger) {
    this.trigger = trigger;
    this.body.innerHTML = '';
    this.body.setAttribute('aria-busy', 'true');
    this.dialog.showModal();

    try {
      const url = new URL(trigger.href, window.location.origin);
      url.searchParams.set('section_id', 'quick-add');

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html');

      // La respuesta viene envuelta en el contenedor de la sección; se coge solo
      // el cuerpo para no meter un `id` duplicado en la página.
      const content = fresh.querySelector('.quick-add');
      if (!content) throw new Error('sin contenido');

      this.body.append(content);
    } catch (error) {
      console.error('[quick-add] No se pudo cargar el producto.', error);
      // Mejor la ficha completa que un diálogo vacío: el cliente sigue pudiendo
      // comprar.
      this.dialog.close();
      window.location.href = trigger.href;
    } finally {
      this.body.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', QuickAddModal);
}
})();
